import { useEffect, useMemo } from 'react';
import { MdClose, MdInsertDriveFile } from "react-icons/md";
import { isImageFile, formatFileSize } from '../helpers/fileValidation';
import { useStrings } from '../i18n/useStrings';
import './file-preview.css';

/**
 * File preview component
 * Shows preview of selected files before sending
 * Supports multiple files with individual remove buttons
 *
 * @param {Array<File>} files - Array of selected files to preview
 * @param {Function} onRemove - Callback to remove a specific file (receives file object)
 */
export default function FilePreview({ files, onRemove }) {
    const strings = useStrings();

    // Create blob URLs for image previews with proper cleanup
    const filePreviewData = useMemo(() => {
        return files.map(file => ({
            file,
            previewUrl: isImageFile(file) ? URL.createObjectURL(file) : null
        }));
    }, [files]);

    // Cleanup blob URLs when component unmounts or files change
    useEffect(() => {
        return () => {
            filePreviewData.forEach(({ previewUrl }) => {
                if (previewUrl) {
                    URL.revokeObjectURL(previewUrl);
                }
            });
        };
    }, [filePreviewData]);

    if (!files || files.length === 0) {
        return null;
    }

    return (
        <div className="filePreviewContainer">
            {filePreviewData.map(({ file, previewUrl }, index) => {
                const isImage = isImageFile(file);
                const fileSize = formatFileSize(file.size);
                
                // Create unique key using file properties
                const fileKey = `${file.name}-${file.size}-${index}`;

                return (
                    <div key={fileKey} className="filePreviewContent">
                        {isImage && previewUrl ? (
                            <div className="filePreviewImage">
                                <img
                                    src={previewUrl}
                                    alt={file.name}
                                />
                            </div>
                        ) : (
                            <div className="filePreviewIcon">
                                <MdInsertDriveFile size={32} />
                            </div>
                        )}

                        <div className="filePreviewInfo">
                            <div className="filePreviewName" title={file.name}>
                                {file.name}
                            </div>
                            <div className="filePreviewSize">
                                {fileSize}
                            </div>
                        </div>

                        <button
                            className="filePreviewRemove"
                            onClick={() => onRemove(file)}
                            title={strings.removeFile}
                            aria-label={strings.removeFile}
                        >
                            <MdClose size={20} />
                        </button>
                    </div>
                );
            })}
        </div>
    );
}
